<template>
  <DetailCard id="detail-landboard" :loading="loading">
    <template #heading>
      <span class="pi pi-map"></span>
      <a :href="getLandBoardExternalUrl" target="_blank">
        {{ $t(`messages.detail.landboard.title`) }}
      </a>
    </template>
    <template #controls>
      <MapHelpTooltip v-if="!loading" size="sm" variant="secondary" />
    </template>

    <div v-if="error">
      <h5 class="card-title">{{ errorMessage }}</h5>
    </div>
    <div v-else>
      <div id="detail-restrictions-map" class="detail-map">
        <MapComponent
          :center="mapview.center"
          :extent="mapview.extent"
          :zoom="mapview.zoom"
          :bbox="mapview.bbox"
          :minZoom="mapview.minZoom"
          :maxZoom="mapview.maxZoom"
          :projection="mapview.projection"
          :baseLayers="mapview.baseLayers"
          :infoLayers="mapview.infoLayers"
          :overlay="mapview.overlay"
          :zoomToFeatures="true"
          :zoomControl="true"
          :twoFingerPanZoom="true"
          @update="exportMap"
          ref="map"
        />
      </div>
      <div class="px-[20px]">
        <div
          v-for="datum in landBoardData"
          :key="datum.field"
          class="grid grid-cols-2 py-3 border-b border-gray-200 dark:border-gray-700"
        >
          <label :for="datum.field" class="font-bold">
            {{ datum.label }}
          </label>
          <PredefinedValuesInputComponent
            v-if="editableFields.includes(datum.field)"
            category="landboard"
            :name="datum.field"
            :append-on-select="true"
            v-model="landBoard[datum.field]"
            @update:model-value="saveEditableField(datum.field, $event)"
          />
          <RatingMultiSelect
            v-else-if="ratingFields.includes(datum.field)"
            v-model="landBoard[datum.field]"
            :field="datum.field"
            :options="[1, 2, 3, 4, 5, 6, 7, 8, 9]"
            :maxSelectedLabels="3"
            :selectionLimit="3"
            placeholder="Tee kuni 3 valikut"
            size="base"
            @update:model-value="saveParking(datum.field, $event)"
          />
          <span v-else>{{ datum.value }}</span>
        </div>
      </div>
    </div>
  </DetailCard>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { computed, onMounted, readonly, ref, shallowRef } from 'vue'
import { useI18n } from 'vue-i18n'
import DetailCard from '@/components/detail/DetailCard.vue'
import MapComponent from '@/components/map/MapComponent.vue'
import MapHelpTooltip from '@/components/map/MapHelpTooltip.vue'
import PredefinedValuesInputComponent from '@/components/PredefinedValuesInputComponent.vue'
import RatingMultiSelect from '@/components/RatingMultiSelect.vue'
import { useDetailMapHelpers } from '@/composables/useDetailMapHelpers'
import {
  DETAIL_LANDBOARD_MAP_FEATURE_LAYER,
  DETAIL_LANDBOARD_MAP_INFO_LAYERS,
  MAP_CENTER,
  MAP_EXTENT,
  MAP_MAXZOOM,
  MAP_MINZOOM,
  MAP_PROJECTION,
} from '@/constants'
import { createGeoJsonFeature } from '@/services/map/FeatureService'
import { transform } from '@/services/map/ProjectionService'
import { useDetailStore } from '@/stores/useDetailStore'
import { useEvaluationStore } from '@/stores/useEvaluationStore'
import { useTransactionStore } from '@/stores/transaction'

const detailStore = useDetailStore()
const evaluationStore = useEvaluationStore()
const transactionStore = useTransactionStore()

const { landBoard, cadasterId } = storeToRefs(detailStore)

const { findLayer } = useDetailMapHelpers()
const { t } = useI18n()

const map = ref(null)
const loading = shallowRef(true)
const error = shallowRef(false)
const errorMessage = shallowRef('')
const fields = readonly([
  'katastritunnus',
  'lahiaadress',
  'asustus',
  'omavalitsus',
  'maakond',
  'registreeritud_kuupev',
  'muudetud_kuupev',
  'sihtotstarve1',
  'sihtotstarve2',
  'sihtotstarve3',
  'katastriyksuse_pindala',
  'ruumikuju_pindala',
  'haritavmaa',
  'looduslik_rohumaa',
  'metsamaa',
  'ouemaa',
  'muu_maa',
  'registriosa',
  'moodustamise_kuupev',
  'mootja_nimi',
  'viljakustsoon',
  'kinnistu_parkimine',
  'kinnistu_kuju',
  'kinnistu_haljastus',
  'kinnistu_veekogu',
  'kinnistu_piirded',
])
const editableFields = readonly([
  // 'kinnistu_parkimine',
  'kinnistu_kuju',
  'kinnistu_haljastus',
  'kinnistu_veekogu',
  'kinnistu_piirded',
])

const ratingFields = readonly([
  'kinnistu_parkimine',
])

const mapview = ref({
  status: true,
  center: transform('fromLonLat', MAP_CENTER, MAP_PROJECTION),
  zoom: 3,
  extent: transform('extent', MAP_EXTENT, 'EPSG:4326', MAP_PROJECTION),
  bbox: null,
  minZoom: MAP_MINZOOM,
  maxZoom: MAP_MAXZOOM,
  projection: MAP_PROJECTION,
  baseLayers: [findLayer('ma-photo')],
  infoLayers: DETAIL_LANDBOARD_MAP_INFO_LAYERS.map(layer => findLayer(layer)),
  overlay: null,
})

const getLandBoardExternalUrl = computed(() => {
  return cadasterId.value
    ? `https://xgis.maaamet.ee/xgis2/page/app/maainfo?searchid=FUUKAT101x2&TUNNUS=${cadasterId.value}&hide=true`
    : '#'
})

const landBoardData = computed(() => {
  return fields.map(field => ({
    label: t(`messages.detail.landboard.${field}`),
    value: landBoard.value[field],
    field: field,
  }))
})

onMounted(() => {
  try {
    if (landBoard.value?.geometry) {
      createMapFeatures(landBoard.value.geometry)
    } else if (landBoard.value?.bbox) {
      mapview.value.bbox = landBoard.value.bbox
    }
  } catch (err) {
    console.error(
      'DetailLandBoardComponent: Error loading land board data:',
      err,
    )
    error.value = true
    errorMessage.value = err
  } finally {
    loading.value = false
  }
})

const createMapFeatures = geometry => {
  const layer = findLayer(DETAIL_LANDBOARD_MAP_FEATURE_LAYER)
  layer.features.push(createGeoJsonFeature(geometry, {}))
  mapview.value.overlay = layer
}

const saveParking = (key, value) => {
  evaluationStore.save({ landboard: { [key]: value } })
  transactionStore.masterData[key] = value
}

// RED-267: kinnistu_haljastus feeds the land kohandustabel master column
// under the `haljastus` key — write through so an already-mounted table
// refreshes live (the saveParking precedent, with key mapping).
const saveEditableField = (key, value) => {
  evaluationStore.save({ landboard: { [key]: value } })
  if (key === 'kinnistu_haljastus' && transactionStore.masterData) {
    transactionStore.masterData.haljastus = value
  }
}

const exportMap = async () => {
  if (!map.value) {
    return
  }
  const mapExport = await map.value.exportMap()
  detailStore.setMapExport({ key: 'landBoard', value: mapExport })
}
</script>

<style>
  #detail-landboard {
    .detail-map {
      position: relative;
      height: 600px;
      width: 100%;
      margin: 0;
    }
  }
</style>
